import { useEffect, useRef } from "react";
import { UploadingStateType } from "./useUploadInProgress";

const POLL_INTERVAL_MS = 5000;

/**
 * Self-heals a "stuck on Processing" upload gate.
 *
 * The local upload gate (uploadingState) is normally cleared only by the one-shot "UnzipCourse"
 * SignalR completion message, which is fire-and-forget and is not replayed to a browser that was
 * disconnected when it fired (a reconnect race, or a silent duplicate Service Bus delivery). When
 * that message is missed the UI stays on "Processing" until a page reload.
 *
 * The durable, Redis-backed processing-status is the source of truth. While this browser believes
 * it is processing, poll that status; once we have positively observed the draft's slot reserved
 * (serverProcessingCount >= 1) and then released (0), synthesise the missed completion so the
 * existing SubmoduleEditor effect clears the UI - exactly as if the real message had arrived.
 *
 * The reserved-then-released ordering is guaranteed safe: the server reserves the slot before the
 * ProcessFile request returns, and this browser only enters "Processing" after that response, so a
 * fresh poll taken while "Processing" always reads >= 1 before it can read 0.
 */
export const useReconcileUploadGate = ({ uploadingState, serverProcessingCount, refreshProcessingStatus, setProcessComplete }) => {
	const hasSeenServerProcessingRef = useRef(false);

	// Poll the durable status while this browser is processing, so a missed SignalR update still
	// resolves without a page reload.
	useEffect(() => {
		if (uploadingState !== UploadingStateType.Processing) {
			hasSeenServerProcessingRef.current = false;
			return;
		}
		refreshProcessingStatus();
		const timer = setInterval(refreshProcessingStatus, POLL_INTERVAL_MS);
		return () => {
			clearInterval(timer);
		};
	}, [uploadingState, refreshProcessingStatus]);

	// Reconcile the local gate against the durable status.
	useEffect(() => {
		if (uploadingState !== UploadingStateType.Processing) {
			return;
		}
		if (serverProcessingCount > 0) {
			hasSeenServerProcessingRef.current = true;
			return;
		}
		// serverProcessingCount === 0: only treat as complete once we have positively observed the
		// slot reserved, so we never fire on a stale pre-upload snapshot.
		if (hasSeenServerProcessingRef.current) {
			setProcessComplete({ success: true, error: "" });
		}
	}, [uploadingState, serverProcessingCount, setProcessComplete]);
};
